// client/src/hooks/useCsgo.js

import { useState, useCallback } from 'react';
import csgoService from '../services/csgoService';

const useCsgo = () => {
  const [overview, setOverview] = useState(null);
  const [items, setItems] = useState([]);
  const [gambling, setGambling] = useState([]);
  const [gamblingStats, setGamblingStats] = useState(null);
  const [portfolioHistory, setPortfolioHistory] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState({});
  const [error, setError] = useState('');

  const withLoading = useCallback(async (key, fn) => {
    setLoading((prev) => ({ ...prev, [key]: true }));
    setError('');
    try {
      return await fn();
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  const fetchOverview = useCallback(
    () =>
      withLoading('overview', async () => {
        const [ov, history] = await Promise.all([
          csgoService.getOverview(),
          csgoService.getPortfolioHistory()
        ]);
        setOverview(ov);
        setPortfolioHistory(history);
        return ov;
      }),
    [withLoading]
  );

  const fetchItems = useCallback(
    () =>
      withLoading('items', async () => {
        const data = await csgoService.getItems();
        setItems(data);
        return data;
      }),
    [withLoading]
  );

  const updateItem = useCallback(
    (id, data) =>
      withLoading('updateItem', async () => {
        const res = await csgoService.updateItem(id, data);
        await csgoService.getItems().then(setItems);
        return res;
      }),
    [withLoading]
  );

  // Cocher/décocher une semaine réglée : mise à jour optimiste de l'historique
  // hebdo (recharger toute la vue d'ensemble pour un booléen ferait tout clignoter).
  const setWeekPaid = useCallback(async (weekStart, paid, amountUsd) => {
    const key = new Date(weekStart).toISOString();
    const patch = (value) =>
      setOverview((prev) =>
        prev && prev.weeklyHistory
          ? {
              ...prev,
              weeklyHistory: prev.weeklyHistory.map((h) =>
                new Date(h.weekStart).toISOString() === key ? { ...h, paid: value } : h
              )
            }
          : prev
      );
    patch(paid);
    setError('');
    try {
      return await csgoService.setWeekPayment(weekStart, paid, amountUsd);
    } catch (err) {
      patch(!paid);
      setError(err.message || 'Impossible de marquer la semaine.');
      throw err;
    }
  }, []);

  const sync = useCallback(() => withLoading('sync', () => csgoService.sync()), [withLoading]);

  const importSteam = useCallback(
    (jsonText) => withLoading('import', () => csgoService.importSteam(jsonText)),
    [withLoading]
  );

  const importCsfloat = useCallback(
    (jsonText) => withLoading('import', () => csgoService.importCsfloat(jsonText)),
    [withLoading]
  );

  const syncCsfloat = useCallback(
    () => withLoading('import', () => csgoService.syncCsfloat()),
    [withLoading]
  );

  const syncSteam = useCallback(
    () => withLoading('import', () => csgoService.syncSteam()),
    [withLoading]
  );

  const [platforms, setPlatforms] = useState([]);

  const fetchPlatforms = useCallback(
    () =>
      withLoading('platforms', async () => {
        const data = await csgoService.getPlatforms();
        setPlatforms(data);
        return data;
      }),
    [withLoading]
  );

  const createPlatform = useCallback(
    (data) =>
      withLoading('savePlatform', async () => {
        const res = await csgoService.createPlatform(data);
        await csgoService.getPlatforms().then(setPlatforms);
        return res;
      }),
    [withLoading]
  );

  const updatePlatform = useCallback(
    (id, data) =>
      withLoading('savePlatform', async () => {
        const res = await csgoService.updatePlatform(id, data);
        await csgoService.getPlatforms().then(setPlatforms);
        return res;
      }),
    [withLoading]
  );

  const deletePlatform = useCallback(
    (id) =>
      withLoading('savePlatform', async () => {
        await csgoService.deletePlatform(id);
        setPlatforms((prev) => prev.filter((p) => p.id !== id));
      }),
    [withLoading]
  );

  const syncPlatform = useCallback(
    (id) =>
      withLoading('syncPlatform', async () => {
        const res = await csgoService.syncPlatform(id);
        await csgoService.getPlatforms().then(setPlatforms);
        return res;
      }),
    [withLoading]
  );

  const fetchGambling = useCallback(
    () =>
      withLoading('gambling', async () => {
        const [list, stats] = await Promise.all([
          csgoService.getGambling(),
          csgoService.getGamblingStats()
        ]);
        setGambling(list);
        setGamblingStats(stats);
        return list;
      }),
    [withLoading]
  );

  const createGambling = useCallback(
    (data) =>
      withLoading('saveGambling', async () => {
        const res = await csgoService.createGambling(data);
        const [list, stats] = await Promise.all([
          csgoService.getGambling(),
          csgoService.getGamblingStats()
        ]);
        setGambling(list);
        setGamblingStats(stats);
        return res;
      }),
    [withLoading]
  );

  const deleteGambling = useCallback(
    (id) =>
      withLoading('deleteGambling', async () => {
        await csgoService.deleteGambling(id);
        const [list, stats] = await Promise.all([
          csgoService.getGambling(),
          csgoService.getGamblingStats()
        ]);
        setGambling(list);
        setGamblingStats(stats);
      }),
    [withLoading]
  );

  const fetchConfig = useCallback(
    () =>
      withLoading('config', async () => {
        const cfg = await csgoService.getConfig();
        setConfig(cfg);
        return cfg;
      }),
    [withLoading]
  );

  const updateConfig = useCallback(
    (data) =>
      withLoading('saveConfig', async () => {
        const cfg = await csgoService.updateConfig(data);
        setConfig(cfg);
        return cfg;
      }),
    [withLoading]
  );

  return {
    overview,
    items,
    gambling,
    gamblingStats,
    portfolioHistory,
    config,
    loading,
    error,
    setError,
    fetchOverview,
    fetchItems,
    updateItem,
    setWeekPaid,
    sync,
    importSteam,
    importCsfloat,
    syncCsfloat,
    syncSteam,
    platforms,
    fetchPlatforms,
    createPlatform,
    updatePlatform,
    deletePlatform,
    syncPlatform,
    fetchGambling,
    createGambling,
    deleteGambling,
    fetchConfig,
    updateConfig
  };
};

export default useCsgo;
